import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { LatestVoyageData } from '../types/ViewModel/LatestVoyageData';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { EventRevision, isNewerEventRevision } from '../app/recovery/eventRecovery';

export type EventDataForVoyages = {
    imoNumber: number;
    voyageNumbers: string[];
    /**
     * HTTP endpoint cursor derived from its lastUpdatedDateUtc response. This
     * remains separate from the SignalR watermark because the endpoint response
     * does not prove which SignalR messages have been successfully applied.
     */
    lastHttpDeltaCursorEpoch?: number;
    /**
     * Server-issued SignalR epoch whose event payload has been accepted by the
     * client merge path. Never set from an HTTP response timestamp.
     */
    lastAppliedSignalREpoch?: number;
};

export interface EventDeltaTransaction {
    id: number;
    events: TankersEvent[];
    imoNumber?: number;
    isCurrent?: () => boolean;
}

export interface EventDeltaCommitResult {
    appliedEventIds: string[];
}

export interface EventRevisionSnapshot {
    generation: number;
    revisions: Map<string, EventRevision>;
}

type EventContextType = {
    fullEventArray: TankersEvent[] | null;
    deltaEventArray: TankersEvent[] | null;
    eventDeltaTransactions: EventDeltaTransaction[];
    selectedEvent: TankersEvent | null;
    savedEvent: TankersEvent | null;
    eventDataForVoyageList: EventDataForVoyages | null;
    lastDeletEventDate: Date | null;
    lastEventChangeDate: Date | null;
    forceEventReloadDate: Date | null;
    setForceEventReloadDate: (date: Date | null) => void;
    lastNotValidatingEvent: TankersEvent | null;
    firstPortCallEventTime: Date | null;
    latestVoyageData: LatestVoyageData | null;
    cargoOperationsEvents: TankersEvent[] | null;
    setFullEventArray: (evList: TankersEvent[]) => void;
    setDeltaEventArray: (evList: TankersEvent[] | null) => void;
    enqueueEventDelta: (evList: TankersEvent[], imoNumber?: number, isCurrent?: () => boolean) => Promise<EventDeltaCommitResult>;
    acknowledgeEventDeltaTransactions: (results: Map<number, EventDeltaCommitResult>) => void;
    getCommittedEventRevisions: (imoNumber: number) => EventRevisionSnapshot;
    commitEventRevisions: (imoNumber: number, generation: number, revisions: ReadonlyMap<string, EventRevision>) => void;
    clearCommittedEventRevisions: () => void;
    setSelectedEvent: (event: TankersEvent | null) => void;
    setSavedEvent: (event: TankersEvent | null) => void;
    setEventDataForVoyageList: (voyList: EventDataForVoyages) => void;
    setLastDeleteEventDate: (boolean) => void;
    setLastEventChangeDate: (date: Date) => void;
    setLastNotValidatingEvent: (event: TankersEvent | null) => void;
    setFirstPortCallEventTime: (date: Date | null) => void;
    setLatestVoyageData: (latestVoyageData: LatestVoyageData | null) => void;
    setCargoOperationsEvents: (events: TankersEvent[] | null) => void;
    /** Count of currently-mounted Event consumers; reactive so effects elsewhere can watch
     * mount/unmount transitions. */
    eventConsumerCount: number;
    registerEventConsumer: () => void;
    unregisterEventConsumer: () => void;
    /** Bumped whenever any vessel's reconciliation-needed flag changes, for effects to watch. */
    eventReconciliationVersion: number;
    isEventReconciliationNeeded: (imoNumber: number) => boolean;
    /**
     * A per-vessel generation counter (not just a boolean) lets a completion callback prove
     * whether it is still clearing the need it observed at attempt-start, or whether a newer
     * mark superseded it while the attempt was in flight — passing the captured generation back
     * to clearEventReconciliationNeeded makes a stale clear a no-op instead of erasing a real need.
     */
    getEventReconciliationGeneration: (imoNumber: number) => number;
    clearEventReconciliationNeeded: (imoNumber: number, expectedGeneration?: number) => void;
};

export const EventContext = React.createContext<EventContextType>(undefined!);

export const EventProvider = ({ children }: ContextProps) => {
    const [fullEventArray, setFullEventArray] = useState<TankersEvent[] | null>(null);
    const [eventDeltaTransactions, setEventDeltaTransactions] = useState<EventDeltaTransaction[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<TankersEvent | null>(null);
    const [savedEvent, setSavedEvent] = useState<TankersEvent | null>(null);
    const [eventDataForVoyageList, setEventDataForVoyageList] = useState<EventDataForVoyages>(null);
    const [lastDeletEventDate, setLastDeleteEventDate] = useState<Date | null>(null);
    const [lastEventChangeDate, setLastEventChangeDate] = useState<Date | null>(null);
    const [forceEventReloadDate, setForceEventReloadDate] = useState<Date | null>(null);
    const [lastNotValidatingEvent, setLastNotValidatingEvent] = useState<TankersEvent | null>(null);
    const [firstPortCallEventTime, setFirstPortCallEventTime] = useState<Date | null>(null);
    const [latestVoyageData, setLatestVoyageData] = useState<LatestVoyageData | null>(null);
    const [cargoOperationsEvents, setCargoOperationsEvents] = useState<TankersEvent[] | null>(null);
    const nextEventDeltaTransactionIdRef = useRef(1);
    const eventDeltaResolversRef = useRef(new Map<number, (result: EventDeltaCommitResult) => void>());
    // Revisions are shared across every Event consumer for the active browser
    // session. A consumer may remount while an earlier HTTP response is pending,
    // so its local lifecycle cannot be the authority for stale-event rejection.
    const committedEventRevisionsByVesselRef = useRef(new Map<number, Map<string, EventRevision>>());
    const eventRevisionGenerationRef = useRef(0);
    // Source of truth for "is any Event consumer mounted right now?" — a ref so enqueueEventDelta
    // (called from the SignalR message path, not from render) reads it synchronously. Mirrored
    // into React state so effects elsewhere can react to changes.
    const eventConsumerCountRef = useRef(0);
    const [eventConsumerCount, setEventConsumerCount] = useState(0);
    // Vessels whose Event state was settled without being applied (no consumer mounted, or a
    // backlog overflow) and need an authoritative HTTP reconciliation once a consumer returns.
    // Keyed by imoNumber -> generation (see getEventReconciliationGeneration) rather than a plain
    // Set, so a stale completion can't clear a need that was re-marked while it was in flight.
    const eventReconciliationGenerationRef = useRef(new Map<number, number>());
    const [eventReconciliationVersion, setEventReconciliationVersion] = useState(0);

    const resolveEventDeltaTransactions = (transactions: readonly EventDeltaTransaction[]): void => {
        transactions.forEach((transaction) => {
            eventDeltaResolversRef.current.get(transaction.id)?.({ appliedEventIds: [] });
            eventDeltaResolversRef.current.delete(transaction.id);
        });
    };

    const markEventReconciliationNeeded = (imoNumber: number): void => {
        const currentGeneration = eventReconciliationGenerationRef.current.get(imoNumber) ?? 0;
        eventReconciliationGenerationRef.current.set(imoNumber, currentGeneration + 1);
        setEventReconciliationVersion((version) => version + 1);
    };

    const isEventReconciliationNeeded = useCallback(
        (imoNumber: number): boolean => eventReconciliationGenerationRef.current.has(imoNumber),
        []
    );

    const getEventReconciliationGeneration = useCallback(
        (imoNumber: number): number => eventReconciliationGenerationRef.current.get(imoNumber) ?? 0,
        []
    );

    const clearEventReconciliationNeeded = useCallback((imoNumber: number, expectedGeneration?: number): void => {
        const currentGeneration = eventReconciliationGenerationRef.current.get(imoNumber);
        if (currentGeneration === undefined) {
            return;
        }
        if (expectedGeneration !== undefined && currentGeneration !== expectedGeneration) {
            // A newer mark (e.g. a fresh eviction) superseded the need this caller observed when
            // its recovery attempt started — that newer need is still real. Do not erase it.
            return;
        }
        eventReconciliationGenerationRef.current.delete(imoNumber);
        setEventReconciliationVersion((version) => version + 1);
    }, []);

    // An unmounted consumer must not let enqueueEventDelta retain an unresolved resolver, which
    // would permanently block the SignalR event-application serialization tail.
    const registerEventConsumer = useCallback((): void => {
        eventConsumerCountRef.current += 1;
        setEventConsumerCount(eventConsumerCountRef.current);
    }, []);

    const unregisterEventConsumer = useCallback((): void => {
        eventConsumerCountRef.current = Math.max(0, eventConsumerCountRef.current - 1);
        setEventConsumerCount(eventConsumerCountRef.current);
        if (eventConsumerCountRef.current > 0) {
            return;
        }
        // The last consumer just unmounted; anything still pending here will never be drained
        // (its owning component is gone) — settle now and mark those vessels for reconciliation
        // so a later mount fetches the missed state authoritatively.
        setEventDeltaTransactions((transactions) => {
            if (transactions.length === 0) {
                return transactions;
            }
            resolveEventDeltaTransactions(transactions);
            transactions.forEach((transaction) => {
                if (transaction.imoNumber !== undefined) {
                    markEventReconciliationNeeded(transaction.imoNumber);
                }
            });
            return [];
        });
    }, []);

    // Transactions are retained and evicted individually rather than coalesced: each carries a
    // distinct isCurrent generation-guard closure, so merging them would let a still-current
    // caller resurrect a superseded session's events through another caller's stale closure.
    // Instead, once a vessel exceeds this many unacknowledged transactions (a consumer mounted
    // but stalled — the zero-consumer case above already short-circuits), the oldest excess is
    // evicted and settled with an empty result.
    const MAX_PENDING_EVENT_DELTA_TRANSACTIONS_PER_VESSEL = 20;

    const enqueueEventDelta = useCallback(
        (events: TankersEvent[], imoNumber?: number, isCurrent?: () => boolean): Promise<EventDeltaCommitResult> => {
            // With no consumer mounted, retaining an unresolved resolver would permanently block
            // the SignalR serialization tail this promise is chained onto. Settle immediately and
            // mark the vessel for reconciliation instead of silently discarding the change.
            if (eventConsumerCountRef.current === 0) {
                if (imoNumber !== undefined) {
                    markEventReconciliationNeeded(imoNumber);
                }
                return Promise.resolve({ appliedEventIds: [] });
            }

            const id = nextEventDeltaTransactionIdRef.current++;
            return new Promise<EventDeltaCommitResult>((resolve) => {
                eventDeltaResolversRef.current.set(id, resolve);
                setEventDeltaTransactions((transactions) => {
                    const next = [...transactions, { id, events, imoNumber, isCurrent }];
                    const vesselTransactions = next.filter((transaction) => transaction.imoNumber === imoNumber);
                    const overflow = vesselTransactions.length - MAX_PENDING_EVENT_DELTA_TRANSACTIONS_PER_VESSEL;
                    if (overflow <= 0) {
                        return next;
                    }
                    const toEvict = vesselTransactions.slice(0, overflow);
                    const evictedIds = new Set(toEvict.map((transaction) => transaction.id));
                    // Deferred to a microtask so this updater — which React may invoke more than
                    // once (e.g. Strict Mode) — stays free of side effects.
                    queueMicrotask(() => {
                        resolveEventDeltaTransactions(toEvict);
                        toEvict.forEach((transaction) => {
                            if (transaction.imoNumber !== undefined) {
                                markEventReconciliationNeeded(transaction.imoNumber);
                            }
                        });
                    });
                    return next.filter((transaction) => !evictedIds.has(transaction.id));
                });
            });
        },
        []
    );

    // Existing producers continue to use this setter. Internally each write becomes a
    // durable transaction so a later React state write cannot replace an unmerged delta.
    const setDeltaEventArray = useCallback(
        (events: TankersEvent[] | null): void => {
            if (events === null) {
                setEventDeltaTransactions((transactions) => {
                    resolveEventDeltaTransactions(transactions);
                    return [];
                });
                return;
            }

            void enqueueEventDelta(events);
        },
        [enqueueEventDelta]
    );

    const acknowledgeEventDeltaTransactions = useCallback((results: Map<number, EventDeltaCommitResult>): void => {
        setEventDeltaTransactions((transactions) => {
            const acknowledged = transactions.filter((transaction) => results.has(transaction.id));
            acknowledged.forEach((transaction) => {
                eventDeltaResolversRef.current.get(transaction.id)?.(results.get(transaction.id)!);
                eventDeltaResolversRef.current.delete(transaction.id);
            });
            return transactions.filter((transaction) => !results.has(transaction.id));
        });
    }, []);

    // Settle (never reject) any transactions still awaiting a real Event consumer when this
    // provider unmounts, so no enqueueEventDelta caller is left permanently pending. Reads
    // eventDeltaResolversRef directly rather than eventDeltaTransactions state, since state only
    // reflects an enqueue once its update has committed — a resolver registered between the last
    // commit and unmount would otherwise be skipped and left pending forever.
    useEffect(() => {
        return () => {
            // Read .current here (not captured at effect setup) so this observes every resolver
            // registered up to the moment of unmount.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const resolvers = eventDeltaResolversRef.current;
            resolvers.forEach((resolve) => resolve({ appliedEventIds: [] }));
            resolvers.clear();
        };
    }, []);

    const getCommittedEventRevisions = useCallback((imoNumber: number): EventRevisionSnapshot => {
        return {
            generation: eventRevisionGenerationRef.current,
            revisions: new Map(committedEventRevisionsByVesselRef.current.get(imoNumber)),
        };
    }, []);

    const commitEventRevisions = useCallback((imoNumber: number, generation: number, revisions: ReadonlyMap<string, EventRevision>): void => {
        if (generation !== eventRevisionGenerationRef.current) {
            return;
        }
        const committedRevisions = committedEventRevisionsByVesselRef.current.get(imoNumber) ?? new Map<string, EventRevision>();
        revisions.forEach((candidate, eventId) => {
            const current = committedRevisions.get(eventId);
            if (!current || isNewerEventRevision(candidate, current)) {
                committedRevisions.set(eventId, candidate);
            }
        });
        committedEventRevisionsByVesselRef.current.set(imoNumber, committedRevisions);
    }, []);

    const clearCommittedEventRevisions = useCallback((): void => {
        eventRevisionGenerationRef.current += 1;
        committedEventRevisionsByVesselRef.current.clear();
    }, []);

    const deltaEventArray = eventDeltaTransactions.length > 0 ? eventDeltaTransactions.flatMap((transaction) => transaction.events) : null;

    return (
        <EventContext.Provider
            value={{
                fullEventArray,
                setFullEventArray,
                deltaEventArray,
                eventDeltaTransactions,
                setDeltaEventArray,
                enqueueEventDelta,
                acknowledgeEventDeltaTransactions,
                getCommittedEventRevisions,
                commitEventRevisions,
                clearCommittedEventRevisions,
                selectedEvent,
                setSelectedEvent,
                eventDataForVoyageList,
                setEventDataForVoyageList,
                savedEvent,
                setSavedEvent,
                lastDeletEventDate,
                setLastDeleteEventDate,
                lastEventChangeDate,
                setLastEventChangeDate,
                forceEventReloadDate,
                setForceEventReloadDate,
                lastNotValidatingEvent,
                setLastNotValidatingEvent,
                firstPortCallEventTime,
                setFirstPortCallEventTime,
                latestVoyageData,
                setLatestVoyageData,
                cargoOperationsEvents,
                setCargoOperationsEvents,
                eventConsumerCount,
                registerEventConsumer,
                unregisterEventConsumer,
                eventReconciliationVersion,
                isEventReconciliationNeeded,
                getEventReconciliationGeneration,
                clearEventReconciliationNeeded,
            }}
        >
            {children}
        </EventContext.Provider>
    );
};

export const useEventStore = () => useContext(EventContext);
